<script>
import FullCalendar from '@fullcalendar/vue3'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import { get, put, post, destroy } from '@rails/request.js';
import TimetableMixin from '../mixins/timetable.vue'

export default {
  components: {
    FullCalendar
  },
  mixins: [TimetableMixin],
  props: {
    eventId: String,
    volunteersUrl: String,
    shiftsUrl: String,
    attendancesUrl: String,
    shiftPositionsUrl: String,
    eventStartDate: String,
    eventEndDate: String,
  },
  data() {
    return {
      calendarOptions: {
        plugins: [ interactionPlugin, bootstrap5Plugin, resourceTimelinePlugin ],
        schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
        initialView: 'resourceTimeline',
        themeSystem: 'bootstrap5',
        height: "auto",
        nowIndicator: true,
        locale: 'de',
        slotDuration: '00:15:00',
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
        },
        slotLabelFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
        },
        headerToolbar: {
          left: 'title',
          right: 'editShifts,addAttendance,removeAttendance toggleSlotDuration prev,next'
        },
        customButtons: {
          toggleSlotDuration: {
            text: this.$i18n.t("timetable.buttons.toggle_slot_duration"),
            click: this.toggleSlotDuration,
            icon: 'arrows-collapse-vertical',
          },
          addAttendance: {
            text: this.$i18n.t("timetable.volunteers.buttons.add_attendance.label"),
            click: this.activateAddAttendanceView,
            icon: 'plus-square',
          },
          removeAttendance: {
            text: this.$i18n.t("timetable.volunteers.buttons.remove_attendance.label"),
            click: this.activateRemoveAttendanceView,
            icon: 'dash-square',
          },
          editShifts: {
            text: this.$i18n.t("timetable.volunteers.buttons.edit_shifts.label"),
            click: this.activateEditShiftsView,
            icon: 'calendar2-range',
          },
        },
        validRange: {
          start: this.eventStartDate,
          end: this.eventEndDate
        },
        events: [{
          start: this.eventStartDate,
          end: this.eventEndDate,
          editable: false,
          display: 'inverse-background',
          backgroundColor: 'grey'
        }],
        eventSources: [
          {
            id: "shifts",
            url: this.shiftsUrl,
          },
          {
            id: "attendances",
            editable: false,
            display: 'background',
            backgroundColor: '#56bf69',
            url: this.attendancesUrl,
          }
        ],
        eventDrop: this.onDropOrResize,
        eventResize: this.onDropOrResize,
        dragRevertDuration: 0,
        selectable: true,
        select: this.onSelection,
        eventLongPressDelay: 300,
        eventClick: this.onClick,
        datesSet: this.onDateChanged,
        resources: this.volunteersUrl,
        resourceGroupField: "divisionName",
        resourceOrder: ['divisionName', 'title'],
        resourceAreaColumns: [{
          field: 'title',
          headerContent: this.$i18n.t("timetable.volunteers.headers.volunteer"),
        }]
      },
      attendanceView: false,
      attendanceCreateOnSelection: null,
    }
  },
  channels: {
    EventsChannel: {
      received(data) {
        switch (data.event) {
          case "shifts_changed":
            for (const volunteerId of data.volunteer_ids) {
              this.refreshShiftsOfVolunteer(volunteerId)
            }
            break;
          case "shift_deleted":
            this.getEventByIdAndSource(data.shift_id, 'shifts')?.remove()
            break;
          case "attendances_changed":
            this.$refs.calendar.getApi().getEventSourceById("attendances").refetch()
            break;
          case "volunteers_changed":
            this.$refs.calendar.getApi().refetchResources()
            break;
        }
      }
    },
  },
  mounted() {
    if (!this.attendancesUrl) {
      // User do not have access to attendances,
      // thus buttons to switch modes between editing shifts or attendances are not needed to be shown
      document.querySelector(".btn-group:has(.fc-editShifts-button)").classList.add("d-none")
    } else {
      document.querySelector(".fc-editShifts-button").classList.add("active")
    }

    // Fullcalendar does not let buttons to have icons _and_ text. Given text is automatically set as labels.
    document.querySelector(".fc-editShifts-button > span").appendChild(
      this.createButtonText(this.$i18n.t("timetable.volunteers.buttons.edit_shifts.text"))
    );
    document.querySelector(".fc-addAttendance-button > span").appendChild(
      this.createButtonText(this.$i18n.t("timetable.volunteers.buttons.add_attendance.text"))
    );
    document.querySelector(".fc-removeAttendance-button > span").appendChild(
      this.createButtonText(this.$i18n.t("timetable.volunteers.buttons.remove_attendance.text"))
    );

    this.$cable.subscribe({
      channel: "EventsChannel",
      eventId: this.eventId,
    });
  },
  methods: {
    activateAddAttendanceView: async function() {
      this.attendanceView = true
      this.attendanceCreateOnSelection = true
      document.querySelector(".fc-addAttendance-button").classList.add("active")
      document.querySelectorAll(".fc-editShifts-button, .fc-removeAttendance-button").forEach(element => {
        element.classList.remove("active")
      });

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateRemoveAttendanceView: async function() {
      this.attendanceView = true
      this.attendanceCreateOnSelection = false
      document.querySelector(".fc-removeAttendance-button").classList.add("active")
      document.querySelectorAll(".fc-editShifts-button, .fc-addAttendance-button").forEach(element => {
        element.classList.remove("active")
      });

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    activateEditShiftsView: async function() {
      this.attendanceView = false
      document.querySelector(".fc-editShifts-button").classList.add("active")
      document.querySelectorAll(".fc-addAttendance-button, .fc-removeAttendance-button").forEach(element => {
        element.classList.remove("active")
      });

      // prevents glitching as the height of shifts change while extending/collapsing
      this.$refs.calendar.getApi().render()
    },
    onDropOrResize: async function(eventResizeOrDropInfo) {
      var shift = eventResizeOrDropInfo.event;

      if (typeof shift.id == 'undefined') {
        eventResizeOrDropInfo.revert();
        return;
      }

      const response = await put(shift.extendedProps.shiftUrl, { body: JSON.stringify({
        shift: {
          starts_at: shift.start.toISOString(),
          ends_at: shift.end.toISOString(),
          volunteer_id: shift.getResources()[0].id
        }
      })})
      if (!response.ok) {
        eventResizeOrDropInfo.revert();
      }
    },
    onSelection: async function(selectionInfo) {
      if (selectionInfo.end - selectionInfo.start < 1800000) {
        // Check against a minimal duration of 30 minutes to skip random clicks directly on the calendar (e.g. to close modal dialogs)
        return
      }

      // switch functions depending on if we are currently editing shifts or attendances.
      if (this.attendanceView) {
        return await this.onAttendanceSelection(selectionInfo) 
      }

      return await this.onShiftSelection(selectionInfo)
    },
    onShiftSelection: async function(selectionInfo) {
      const response = await get(this.shiftPositionsUrl, { query: {
        "shift[starts_at]": selectionInfo.start.toISOString(),
        "shift[ends_at]": selectionInfo.end.toISOString(),
        "shift[volunteer_id]": selectionInfo.resource.id,
      }})
      if (!response.ok) {
        return
      }

      const content = await response.text
      $("#app-modal .modal-content").html(content)

      bootstrap.Modal.getOrCreateInstance("#app-modal").show()
    },
    onAttendanceSelection: async function(selectionInfo) {
      const post_or_destroy = this.attendanceCreateOnSelection ? post : destroy
      await post_or_destroy(selectionInfo.resource.extendedProps.attendanceUrl, { query: {
        "attendance[starts_at]": selectionInfo.start.toISOString(),
        "attendance[ends_at]": selectionInfo.end.toISOString(),
      }})
    }
  }
}
</script>
<template>
  <FullCalendar :options="calendarOptions" ref="calendar">
    <template v-slot:resourceGroupLabelContent='arg'>
      {{ arg.groupValue }}
    </template>

    <template v-slot:resourceLabelContent='arg'>
      <span class="bi-icon bi-circle-fill me-1" :style="{color: arg.resource.extendedProps.color}"></span>
      <div style="display: table-cell">

        <template v-if="arg.resource.extendedProps.togglePresentUrl">
          <a :href="arg.resource.extendedProps.togglePresentUrl" class="btn btn-sm btn-outline-light border rounded me-1 p-0" rel="nofollow" data-remote="true" data-method="patch">
            <span v-if="arg.resource.extendedProps.isPresent" class="bi bi-person-fill-check text-success me-1" :title="this.$i18n.t('timetable.volunteers.present')"></span>
            <span v-else class="bi bi-person-fill-slash text-danger me-1" :title="this.$i18n.t('timetable.volunteers.absent')"></span>
          </a>
        </template>
        <template v-else>
          <span v-if="arg.resource.extendedProps.isPresent" class="bi bi-person-fill-check text-success me-1" :title="this.$i18n.t('timetable.volunteers.present')"></span>
          <span v-else class="bi bi-person-fill-slash text-danger me-1" :title="this.$i18n.t('timetable.volunteers.absent')"></span>
        </template>

        <a :href="arg.resource.extendedProps.url">{{ arg.resource.title }}</a>
        <span v-if="arg.resource.extendedProps.comment" class="small"><br />{{ arg.resource.extendedProps.comment }}</span>
      </div>
    </template>

    <template v-slot:eventContent='arg'>
      <VueTimetableShift :event="arg.event" v-if="!this.attendanceView" />
    </template>
  </FullCalendar>
</template>
